import {
  Flex,
  IconButton,
  Link,
  Tooltip,
  useDisclosure,
} from "@chakra-ui/react";
import { ColumnDef } from "@tanstack/table-core";
import { useCallback, useMemo, useState } from "react";
import {
  RiCheckLine,
  RiCloseCircleLine,
  RiCurrencyLine,
  RiHistoryLine,
  RiNewspaperLine,
  RiShareBoxLine,
} from "react-icons/ri";
import { Link as RouterLink } from "react-router-dom";
import {
  StatusTag,
  StatusTagMetadata,
} from "src/components/DataDisplay/StatusTag";
import { NoDataTable } from "src/components/Feedback/NoDataTable";
import { WithUserPermissions } from "src/components/Permissions/WithUserPermissions";
import { PaginatedTable } from "src/components/Table/PaginatedTable";
import { SortType } from "src/components/Table/SortButton";
import { TextWithOverflowTooltip } from "src/components/TextWithOverflowTooltip";
import { ListPaymentsResponse } from "src/generated/hooks/form-service/requests/types.gen";
import { useOrganization } from "src/hooks/useOrganization";
import * as Url from "src/services/url";
import { ReceiptModal } from "./ReceiptModal";

type PaymentRow = ListPaymentsResponse["payments"][number];

interface PaymentActivityListProps {
  payments: PaymentRow[];
  limit: number;
  offset: number;
  count: number;
  onFetchMore: (limit: number, offset: number) => void;
  onChangeSort: (columnName: string, sortType: SortType) => void;
  onClearSearch: () => void;
  onCancel: (paymentId: string) => void;
  isLoading?: boolean;
}

const getPaymentStatusMetadata = (status: string): StatusTagMetadata => {
  switch (status) {
    case "paid":
      return {
        label: "Paid",
        longLabel: "Paid",
        colorScheme: "green",
        variant: "subtle",
        icon: RiCheckLine,
      };
    case "pending":
      return {
        label: "Pending",
        longLabel: "Pending",
        colorScheme: "orange",
        variant: "subtle",
        icon: RiHistoryLine,
      };
    case "canceled":
      return {
        label: "Canceled",
        longLabel: "Canceled",
        colorScheme: "gray",
        variant: "subtle",
        icon: RiCloseCircleLine,
      };
    default:
      return {
        label: status,
        longLabel: status,
        colorScheme: "gray",
        variant: "subtle",
      };
  }
};

const formatDate = (dateString: string | null): string => {
  if (!dateString) return "";
  const date = new Date(dateString);
  return `${String(date.getMonth() + 1).padStart(2, "0")}/${String(
    date.getDate()
  ).padStart(2, "0")}/${date.getFullYear()}`;
};

export const PaymentActivityList: React.FC<PaymentActivityListProps> = ({
  payments,
  limit,
  offset,
  count,
  onFetchMore,
  onChangeSort,
  onClearSearch,
  onCancel,
  isLoading = false,
}) => {
  const organization = useOrganization();
  const { isOpen, onOpen, onClose } = useDisclosure();
  const [receiptPaymentId, setReceiptPaymentId] = useState<string | null>(null);

  const columns: ColumnDef<PaymentRow>[] = useMemo(
    () => [
      {
        id: "first_name",
        header: "STUDENT NAME",
        cell: (props) => {
          const person = props.row.original.person;
          if (!person) return null;
          return (
            <Link
              as={RouterLink}
              color="primary.500"
              to={Url.OrgAdmin.Students.edit(organization, person.id)}
              textDecoration="underline"
            >
              {person.firstName}
            </Link>
          );
        },
      },
      {
        id: "last_name",
        header: "STUDENT LASTNAME",
        cell: (props) => {
          const person = props.row.original.person;
          if (!person) return null;
          return (
            <Link
              as={RouterLink}
              color="primary.500"
              to={Url.OrgAdmin.Students.edit(organization, person.id)}
              textDecoration="underline"
            >
              {person.lastName}
            </Link>
          );
        },
      },
      {
        id: "payment",
        header: "PAYMENT",
        cell: (props) => (
          <TextWithOverflowTooltip
            content={props.row.original.paymentTemplate.name}
            maxWidth="20em"
          />
        ),
      },
      {
        id: "status",
        header: "STATUS",
        cell: (props) => (
          <StatusTag
            disableGlossary
            status={getPaymentStatusMetadata(props.row.original.paymentStatus)}
          />
        ),
      },
      {
        id: "status_date",
        header: "STATUS DATE",
        cell: (props) =>
          formatDate(props.row.original.paidAt ?? props.row.original.updatedAt),
      },
      {
        id: "actions",
        header: "ACTIONS",
        cell: (props) => {
          const payment = props.row.original;
          let cancelTooltip = "Cancel";

          if (!payment.canCancel) {
            cancelTooltip =
              payment.paymentStatus === "paid"
                ? "This payment can't be canceled because it has been paid"
                : payment.paymentStatus === "pending"
                ? "This payment can't be canceled because payment processing has already started"
                : "Only pending payments can be canceled";
          }
          return (
            <Flex direction="row" gap="2">
              <WithUserPermissions permissions={["payments:update"]}>
                <Tooltip label={cancelTooltip} placement="left" hasArrow>
                  <IconButton
                    aria-label="Cancel payment"
                    icon={<RiCloseCircleLine />}
                    variant="outline"
                    colorScheme="gray"
                    onClick={() => onCancel(payment.id)}
                    isDisabled={!payment.canCancel}
                  />
                </Tooltip>
              </WithUserPermissions>

              <Tooltip label="Receipt" placement="left" hasArrow>
                <IconButton
                  aria-label="Receipt"
                  icon={<RiNewspaperLine />}
                  variant="outline"
                  colorScheme="gray"
                  onClick={() => {
                    setReceiptPaymentId(payment.id);
                    onOpen();
                  }}
                  isDisabled={payment.paymentStatus === "canceled"}
                />
              </Tooltip>

              <Tooltip label="View in stripe" placement="left" hasArrow>
                <IconButton
                  as={Link}
                  aria-label="View"
                  icon={<RiShareBoxLine />}
                  variant="outline"
                  colorScheme="gray"
                  href={payment.stripeDashboardUrl ?? undefined}
                  target="_blank"
                  rel="noopener noreferrer"
                  isDisabled={!payment.stripeDashboardUrl}
                />
              </Tooltip>
            </Flex>
          );
        },
      },
    ],
    [onCancel, onOpen, organization]
  );

  const handleChangeSort = useCallback(
    (headerId: string, sortType: SortType) => {
      onChangeSort(headerId, sortType);
    },
    [onChangeSort]
  );

  if (isLoading) {
    return (
      <PaginatedTable<PaymentRow>
        data={[]}
        columns={columns}
        limit={limit}
        offset={offset}
        count={0}
        onFetchMore={onFetchMore}
        sortableColumnIds={[
          "first_name",
          "last_name",
          "payment",
          "status",
          "status_date",
        ]}
        onChangeSort={handleChangeSort}
        isLoading={true}
      />
    );
  }

  return (
    <>
      {payments.length ? (
        <PaginatedTable<PaymentRow>
          data={payments}
          columns={columns}
          limit={limit}
          offset={offset}
          count={count}
          onFetchMore={onFetchMore}
          sortableColumnIds={[
            "first_name",
            "last_name",
            "payment",
            "status",
            "status_date",
          ]}
          onChangeSort={handleChangeSort}
        />
      ) : (
        <NoDataTable
          icon={RiCurrencyLine}
          text="No payment activities found"
          buttonText="Clear"
          onButtonAction={onClearSearch}
        />
      )}
      <ReceiptModal
        isOpen={isOpen}
        onClose={onClose}
        paymentId={receiptPaymentId}
      />
    </>
  );
};
